<template>
  <div id="topLevelLayout">
    <div class="flexConstraint">
      <div class="contentContainer">
        <HeaderBar v-if="!isGuestUser && !isServerMessage"
                   v-show="!hideHeaderBar"
                   @clearAllNotifications="showClearAllNotifications = true" />

        <div class="pushPrompt" v-if="showPushPermissionRequest">
          <PushNotificationPermissionBanner />
        </div>

        <div class="bodyConstraint">
          <div class="mainArea">
            <div class="contentArea">
              <slot name="content"></slot>
              <transition name="fade" mode="out-in">
                <FileViewDialog v-if="currentlyDisplayedFile"
                                @close="setCurrentlyDisplayedFile(null)"
                                :entry="currentlyDisplayedFile" />
              </transition>
              <transition name="fade" mode="out-in">
                <ChatSummaryDialog v-if="currentlyDisplayedChatSummaryTrack"
                                   :track="currentlyDisplayedChatSummaryTrack"
                                   @close="setCurrentlyDisplayedChatSummaryTrack(null)" />
              </transition>
            </div>
          </div>

          <div class="privacyMode" v-if="privacyMode">
            <div class="message">
              <h1>Privacy mode is enabled</h1>
              <p>Disable privacy mode on the top bar to access your content</p>
            </div>
          </div>
        </div>
        <div
          v-if="showFooterMenu"
          v-show="!hideHeaderBar"
          class="footer"
          :class="{sideMenuOpen}">
          <NonAppMenu @clearAllNotifications="showClearAllNotifications = true"
                      :is-header="false" />
        </div>
        <ConfirmDialog v-if="showClearAllNotifications"
                       @cancel="showClearAllNotifications = false"
                       @confirm="clearAllNotifications"
                       :isDangerousOperation="true"
                       bodyText="This will clear all of your notifications">
        </ConfirmDialog>
        <ConfirmDialog v-if="pushNotificationStillDenied"
                       :headerText="'Check Browser Settings'"
                       :bodyText="pushNotificationStillDeniedMsg"
                       :confirmOnly="true"
                       @confirm="clearPushNotificationStillDenied"></ConfirmDialog>
        <div id="portal-dock-panel-target"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, defineAsyncComponent, Ref, ref } from 'vue';

import ChatSummaryDialog from '@/components/dialogs/ChatSummaryDialog.vue';
import { ViewableEntry } from '@/components/files/files.types';
import PushNotificationPermissionBanner from '@/components/pushmessaging/PushNotificationPermissionBanner.vue';
import { useRoute } from '@/composables/router/VueRouter';
import { AppName } from '@/data/Branding';
import BrowserDetection from '@/data/BrowserDetection';
import { Track } from '@/data/datatypes/Track';
import HeaderBar from '@/HeaderBar.vue';
import NonAppMenu from '@/NonAppMenu.vue';
import RouteNames from '@/router/RouteNames';
import { useKnowledgeBaseStore } from '@/stores/KnowledgeBase';
import { usePushNotificationStore } from '@/stores/PushNotification';
import { useTrackEntriesStore } from '@/stores/TrackEntries';
import { useTracksStore } from '@/stores/Tracks';
import { useUIStateStore } from '@/stores/UIState';
import { useUserStore } from '@/stores/User';

const FileViewDialog = defineAsyncComponent(() => import('@/components/dialogs/FileViewDialog.vue'));
const ConfirmDialog = defineAsyncComponent(() => import('@/components/dialogs/ConfirmDialog.vue'));

const tracksStore = useTracksStore();
const trackEntriesStore = useTrackEntriesStore();
const userStore = useUserStore();
const uiStateStore = useUIStateStore();
const pushNotificationStore = usePushNotificationStore();
const knowledgeBaseStore = useKnowledgeBaseStore();

const route = useRoute();

interface Props {
  inPhoneLandscapeMode?: boolean;
}
withDefaults(defineProps<Props>(), {
  inPhoneLandscapeMode: false,
});

const showClearAllNotifications: Ref<boolean> = ref(false);

const pushNotificationStillDenied: ComputedRef<boolean> = computed(() => {
  return pushNotificationStore.pushNotificationStillDenied;
});

function clearPushNotificationStillDenied(): Promise<void> {
  return pushNotificationStore.clearPushNotificationStillDenied();
}

const privacyMode: ComputedRef<boolean> = computed(() => {
  return uiStateStore.privacyMode;
});

const hideHeaderBar: ComputedRef<boolean> = computed(() => {
  return uiStateStore.hideHeaderBar;
});

const sideMenuOpen: ComputedRef<boolean> = computed(() => {
  return uiStateStore.sideMenuOpen;
});

const isGuestUser: ComputedRef<boolean> = computed(() => {
  return userStore.isGuestUser;
});

const currentlyDisplayedChatSummaryTrack: ComputedRef<Track | null> = computed(() => {
  return tracksStore.currentlyDisplayedChatSummaryTrack;
});

const currentlyDisplayedFile: ComputedRef<ViewableEntry | null> = computed(() => {
  return trackEntriesStore.currentlyDisplayedFile;
});

const isMobile: ComputedRef<boolean> = computed(() => {
  return BrowserDetection.isMobile();
});

const showFooterMenu: ComputedRef<boolean> = computed(() => {
  return isMobile.value && BrowserDetection.isSupportedBrowser();
});

const pushNotificationStillDeniedMsg: ComputedRef<string> = computed(() => {
  return `${AppName} cannot sent push notifications to this device. To allow ${AppName} to push notifications,' +
    ' update your browser notification settings.`;
});

function setCurrentlyDisplayedChatSummaryTrack(track: Track | null): void {
  return tracksStore.setCurrentlyDisplayedChatSummaryTrack(track);
}

function clearAllNotifications(): void {
  showClearAllNotifications.value = false;
  tracksStore.clearAllUnseenTrackActivity();
  knowledgeBaseStore.clearAllKnowledgeBaseNotifications();
}

const isServerMessage: ComputedRef<boolean> = computed(() => {
  return route.name === RouteNames.SERVER_MESSAGE_ROUTE_NAME;
});

const showPushPermissionRequest: ComputedRef<boolean> = computed(() => {
  if (isGuestUser.value) {
    return false;
  }

  // If the browser doesn't support notifications, or it was dismissed on a previous app load (flagged in local
  // storage) don't show the banner.
  if (pushNotificationStore.pushNotificationState === null ||
      !pushNotificationStore.pushNotificationState.supported ||
      pushNotificationStore.pushBannerDismissed) {
    return false;
  }

  // Only show the permissions required banner if the user has notifications enabled (in their CafeX settings)
  // for the current device type.
  const isMobileDevice = BrowserDetection.isMobile();
  const userWantsNotificationsForThisDeviceType = userStore.currentUser &&
    ((userStore.currentUser.chatMobilePushNotifications && isMobileDevice) ||
    (userStore.currentUser.chatDesktopPushNotifications && !isMobileDevice));

  // If their settings suggest they want notifications here, show the banner if we need to
  if (userWantsNotificationsForThisDeviceType) {
    return !pushNotificationStore.pushBannerDismissed && !pushNotificationStore.pushNotificationState.enabled;
  } else {
    return false;
  }
});

function setCurrentlyDisplayedFile(file: ViewableEntry | null): void {
  trackEntriesStore.setCurrentlyDisplayedFile(file);
}
</script>

<style scoped lang="scss">
#topLevelLayout {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  width: 100%;
}

.headerBar {
  flex: 0 0;
}

.primaryNav {
  width: 4rem;
  min-width: 4rem;
}

.bodyConstraint {
  position: relative;
  flex: 1 1;
  height: inherit;
}

.privacyMode {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(6px);
  // Deliberately incredibly high as this should obscure all content
  z-index: 100000;

  .message {
    background-color: $colour-background-light;
    border: 1px solid $colour-border-mid;
    border-radius: $size-border-radius-primary;
    padding: 1.5rem;

    h1 {
      font-size: 1rem;
      margin: 0;
      margin-bottom: 0.5rem;
    }

    p {
      font-size: $size-font-small;
    }
  }
}

@supports not ((backdrop-filter: blur(6px)) or (-webkit-backdrop-filter: blur(6px))) {
  .privacyMode {
    background-color: $colour-background-light;
  }
}

.footer {
  height: 4.125rem;
  background-color: $colour-background-light;
  border-top: 1px solid #F4F5F8; // TODO: variable

  .nativeIos & {
    padding-bottom: env(safe-area-inset-bottom);
  }
  .mobile & {
    transition: 0.25s ease-in-out;
    opacity: 1;
  }
  .mobile .sideMenuOpen &{
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }
}

.mainArea {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;

  .contentArea {
    flex: 1 1;
    overflow: hidden;
    position: relative;
  }
}
.flexConstraint {
  height: inherit;
  // Note: uncommenting the following results in a blank white page
  //position: relative;
  flex: 1 1;
}
.contentContainer {
  width: 100%;
  height: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
}
.pushPrompt {
  width: 100%;
  min-height: 2rem;
  position: relative;
}
</style>
